<script setup lang="ts">
import { computed, markRaw, ref } from 'vue'

import IconSettingLayout from '@/components/atoms/icons/IconSettingLayout.vue'
import IconSettingText from '@/components/atoms/icons/IconSettingText.vue'
import SettingMargin from '@/components/atoms/SettingMargin.vue'
import SettingPadding from '@/components/atoms/SettingPadding.vue'
import SettingWidth from '@/components/atoms/SettingWidth.vue'
import EditorSidebarLayout from '@/components/layouts/EditorSidebarLayout.vue'
import SettingsLayout from '@/components/layouts/SettingsLayout.vue'
import BetaSettingColorPicker from '@/components/beta-editor/settings/controls/BetaSettingColorPicker.vue'
import BetaSettingFontSize from '@/components/beta-editor/settings/controls/BetaSettingFontSize.vue'
import BetaSettingFontWeight from '@/components/beta-editor/settings/controls/BetaSettingFontWeight.vue'
import BetaSettingTextAlign from '@/components/beta-editor/settings/controls/BetaSettingTextAlign.vue'
import { useEditorBetaStore } from '@/stores/editor/beta'
import { useEditorDefaultStore } from '@/stores/editor/default'
import { useEditorPageStore } from '@/stores/editor/page'
import { useLayoutStore } from '@/stores/layout'
import {
    getTitleDefaultFontSizePx,
    getTitleDefaultFontWeight,
    getTitleDefaultTextAlign,
} from '@/utils/editor-title-defaults'

const betaStore = useEditorBetaStore()
const defaultStore = useEditorDefaultStore()
const editorPageStore = useEditorPageStore()
const layoutStore = useLayoutStore()

const elementForEdit = computed(() => betaStore.elementForEdit)
const sectionExtra = computed(() => {
    const sectionIndex = elementForEdit.value?.sectionIndex

    if (sectionIndex === undefined || sectionIndex === null) return null

    return editorPageStore.activePage?.content?.[sectionIndex]?.extra ?? null
})
const selectedSchema = computed(() => elementForEdit.value?.schema)
const isTopLevelTitle = computed(() => {
    const schema = selectedSchema.value

    if (!Array.isArray(schema) || schema.length !== 2) return false

    const currentLevel = schema[schema.length - 1]

    return currentLevel?.element === 'title'
})
const firstTopLevelTitleIndex = computed(() => {
    const sectionIndex = elementForEdit.value?.sectionIndex

    if (sectionIndex === undefined || sectionIndex === null) return null

    const sectionContent = editorPageStore.activePage?.content?.[sectionIndex]?.frameContent

    if (!Array.isArray(sectionContent)) return null

    return sectionContent.findIndex((frame: any) => frame?.frameType === 'title')
})
const isSiteClientManagedPrimaryTitle = computed(() => {
    if (!isTopLevelTitle.value) return false

    const schema = selectedSchema.value
    if (!Array.isArray(schema)) return false

    const currentLevel = schema[schema.length - 1]

    return firstTopLevelTitleIndex.value !== -1
        && firstTopLevelTitleIndex.value !== null
        && currentLevel?.index === firstTopLevelTitleIndex.value
})
const defaultTitleFontSize = computed(() => getTitleDefaultFontSizePx(
    sectionExtra.value,
    defaultStore.newSection.extra,
    defaultStore.newElementTitle.extra.style.fontSize,
))
const defaultTitleFontWeight = computed(() => getTitleDefaultFontWeight(
    sectionExtra.value,
    layoutStore.getSectionFontWeightDefault(elementForEdit.value?.sectionIndex ?? 0)
    || defaultStore.newElementTitle.extra.style.fontWeight,
))
const defaultTitleTextAlign = computed(() => getTitleDefaultTextAlign(
    sectionExtra.value,
    defaultStore.newElementTitle.extra.style.textAlign,
))
const titleFontSizeLock = computed(() => {
    if (isSiteClientManagedPrimaryTitle.value) return true

    return elementForEdit.value?.extra?.style_font_size_lock
})
const settings = ref([
    { id: 1, name: 'title', icon: markRaw(IconSettingText) },
    { id: 2, name: 'layout', icon: markRaw(IconSettingLayout) },
])

const onChangeWidth = (event: any) => {
    if (!elementForEdit.value) return

    if (event.target === 'desktop') elementForEdit.value.grid = event.value
    if (event.target === 'mobile') elementForEdit.value.gridMobile = event.value

    betaStore.updateElementForEdit()
}

const onChangeStyle = (event: any) => {
    if (!elementForEdit.value) return
    if (
        isSiteClientManagedPrimaryTitle.value
        && ['font-size', 'style_font_size_lock'].includes(event.target)
    ) return

    if (event.target === 'font-size') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            style: {
                ...elementForEdit.value.extra?.style,
                fontSize: event.value,
            },
        }
    }

    if (event.target === 'font-weight') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            style: {
                ...elementForEdit.value.extra?.style,
                fontWeight: event.value,
            },
        }
    }

    if (event.target === 'text-align') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            style: {
                ...elementForEdit.value.extra?.style,
                textAlign: event.value,
            },
        }
    }

    if (event.target === 'style_font_size_lock') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            style_font_size_lock: event.value,
        }
    }

    if (event.target === 'style_font_weight_lock') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            style_font_weight_lock: event.value,
        }
    }

    if (event.target === 'style_text_align_lock') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            style_text_align_lock: event.value,
        }
    }

    betaStore.updateElementForEdit()
}

const onChangeColor = (value: string) => {
    if (!elementForEdit.value) return

    elementForEdit.value.extra = {
        ...elementForEdit.value.extra,
        style: {
            ...elementForEdit.value.extra?.style,
            color: value,
        },
    }

    betaStore.updateElementForEdit()
}

const onChangeMargin = (event: any) => {
    if (!elementForEdit.value) return

    if (event.config === 'desktop') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            margin: {
                ...elementForEdit.value.extra?.margin,
                [event.target]: event.value,
            },
        }
    }

    if (event.config === 'mobile') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            margin_mobile: {
                ...elementForEdit.value.extra?.margin_mobile,
                [event.target]: event.value,
            },
        }
    }

    if (event.config === 'lock') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            margin_mobile_lock: event.value,
        }
    }

    betaStore.updateElementForEdit()
}

const onChangePadding = (event: any) => {
    if (!elementForEdit.value) return

    if (event.config === 'desktop') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            padding: {
                ...elementForEdit.value.extra?.padding,
                [event.target]: event.value,
            },
        }
    }

    if (event.config === 'mobile') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            padding_mobile: {
                ...elementForEdit.value.extra?.padding_mobile,
                [event.target]: event.value,
            },
        }
    }

    if (event.config === 'lock') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            padding_mobile_lock: event.value,
        }
    }

    betaStore.updateElementForEdit()
}
</script>

<template>
    <editor-sidebar-layout v-if="elementForEdit" :settings="settings">
        <template #title>
            <settings-layout name="Configurações do Título" :default-open="true">
                <beta-setting-color-picker
                    label="Cor do Texto"
                    :value="elementForEdit.extra?.style?.color"
                    :default-value="layoutStore.getSectionTextColorDefault(elementForEdit.sectionIndex) ?? ''"
                    clear-on-default
                    @change="onChangeColor"
                />

                <beta-setting-font-size
                    label="Tamanho da Fonte"
                    :value="elementForEdit.extra?.style?.fontSize"
                    :value-default="defaultTitleFontSize"
                    :lock="titleFontSizeLock"
                    :show-switch="true"
                    :disabled="isSiteClientManagedPrimaryTitle"
                    @change="onChangeStyle"
                />

                <beta-setting-font-weight
                    label="Espessura da Fonte"
                    :value="elementForEdit.extra?.style?.fontWeight"
                    :value-default="defaultTitleFontWeight"
                    :lock="elementForEdit.extra?.style_font_weight_lock"
                    :show-switch="true"
                    @change="onChangeStyle"
                />

                <beta-setting-text-align
                    label="Alinhamento"
                    :value="elementForEdit.extra?.style?.textAlign"
                    :value-default="defaultTitleTextAlign"
                    :lock="elementForEdit.extra?.style_text_align_lock"
                    :show-switch="true"
                    @change="onChangeStyle"
                />
            </settings-layout>
        </template>

        <template #layout>
            <settings-layout name="Layout do Título" :default-open="true">
                <setting-width
                    label="Largura"
                    :value="elementForEdit.grid"
                    :value-mobile="elementForEdit.gridMobile"
                    :values-permission-desktop="[4, 6, 12]"
                    :values-permission-mobile="[4, 6, 12]"
                    @change="onChangeWidth"
                />
            </settings-layout>

            <settings-layout name="Avançado">
                <setting-margin
                    label="Margem Externa"
                    :value="elementForEdit.extra?.margin"
                    :value-mobile="elementForEdit.extra?.margin_mobile"
                    :lock="elementForEdit.extra?.margin_mobile_lock"
                    @change="onChangeMargin"
                />

                <setting-padding
                    label="Margem Interna"
                    :value="elementForEdit.extra?.padding"
                    :value-mobile="elementForEdit.extra?.padding_mobile"
                    :lock="elementForEdit.extra?.padding_mobile_lock"
                    @change="onChangePadding"
                />
            </settings-layout>
        </template>
    </editor-sidebar-layout>
</template>
